              
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PieChart } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PortfolioHeaderNew as PortfolioHeader } from "./PortfolioHeaderNew";
import { PortfolioCard } from "./PortfolioCard";
import { Portfolio } from "@/types/portfolio";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { PortfolioTable } from "./PortfolioTable";
import { PortfolioBulkActionsToolbar } from "./PortfolioBulkActionsToolbar";
import { PageContainer } from "@/components/ui/PageContainer";
import { MobileViewportWarning, useIsMobileViewport } from "@/components/ui/MobileViewportWarning";
import { fetchEnhancedPortfolios } from "@/services/portfolio/PortfolioQueryService";
import { createPortfolio, deletePortfoliosBulk, convertPortfolioToModel } from "@/services/portfolio/PortfolioService";
import { insertAllocationsBulk } from "@/services/portfolio/PortfolioAllocationsService";
import { useAuthStore } from "@/stores/authStore";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

type PortfolioViewMode = 'cards' | 'table';

// Define types for allocations and fund data
type Allocation = {
  id: string;
  portfolio_id: string;
  asset_id: string;
  asset_type: string;
  allocation_percentage: number;
  allocation_value?: number | null;
  is_value_locked?: boolean | null;
  currency_code?: string | null;
  created_at: string;
  updated_at: string;
  fund?: any; // Will be populated with fund data
};

type FundData = {
  id: string;
  asset_class?: string;
  sa_asset_class?: string;
  fund_name?: string;
  investment_strategy?: string;
  source?: 'funds' | 'funds_raw';
};

type FundMap = { [key: string]: FundData };

interface EnhancedPortfolio extends Portfolio {
  portfolio_allocations?: Allocation[];
}

export function PortfolioList() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [viewMode, setViewMode] = useState<PortfolioViewMode>('cards');
  const isMobile = useIsMobile();
  const isMobileViewport = useIsMobileViewport(640);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const { data: portfolios, isLoading } = useQuery<EnhancedPortfolio[]>({
    queryKey: ['portfolios'],
    queryFn: async () => await fetchEnhancedPortfolios(),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { user } = useAuthStore();

  const createNewPortfolio = async (
    nameOrEvent?: any,
    portfolioType: string = 'model'
  ) => {
    try {
      // Defensive: handle accidental event param
      const name: string =
        typeof nameOrEvent === 'string' && nameOrEvent.trim().length > 0
          ? nameOrEvent
          : 'New Portfolio';
      if (typeof nameOrEvent !== 'string') {
        console.warn('createNewPortfolio: non-string first arg, defaulting name.', nameOrEvent);
      }

      const uid = user?.id;
      if (!uid) throw new Error('No user logged in');

      const id = await createPortfolio({ userId: uid, name, portfolioType, portfolioSize: 100000, status: 'in_progress' });

      // Invalidate portfolios query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      
      // Also invalidate any portfolio-specific queries
      await queryClient.invalidateQueries({ queryKey: ['portfolio', id] });

      navigate(`/portfolios/build/${id}`);
    } catch (error) {
      console.error('Error creating portfolio:', error);
      toast({
        title: "Error",
        description: "Failed to create portfolio. Please try again.",
        variant: "destructive",
      });
    }
  };

  // (moved below after dependencies are declared)

  // Use the Portfolio interface imported from types

  const filteredPortfolios = portfolios?.filter((portfolio: EnhancedPortfolio) => {
    const matchesSearch = portfolio.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || portfolio.portfolio_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const isEmpty = (portfolios?.length || 0) === 0;

  // Bulk selection state (client-side)
  const [bulkSelectionState, bulkSelectionActions] = useBulkSelection(filteredPortfolios?.length || 0);

  // Single-selection derived state for Convert to Model (after dependencies are declared)
  const selectedIds = Array.from(bulkSelectionState.selectedIds || new Set<string>());
  const selectedPortfolio = selectedIds.length === 1 ? (filteredPortfolios || []).find(p => p.id === selectedIds[0]) : undefined;
  const canConvertToModel = !!selectedPortfolio
    && (selectedPortfolio.portfolio_type === 'personal' || selectedPortfolio.portfolio_type === 'client')
    && Array.isArray(selectedPortfolio.portfolio_allocations)
    && (selectedPortfolio.portfolio_allocations as any[])?.some(a => ((Number(a?.allocation_percentage) || 0) > 0) || ((Number(a?.allocation_value) || 0) > 0));

  const handleConvertSelected = async () => {
    if (!selectedPortfolio || !canConvertToModel) return;
    try {
      const uid = user?.id;
      if (!uid) throw new Error('No user logged in');
      const newId = await convertPortfolioToModel({ originalId: selectedPortfolio.id, userId: uid });
      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      toast({ title: 'Converted to model', description: 'Opening new model portfolio...' });
      navigate(`/portfolios/build/${newId}`);
    } catch (e) {
      console.error('Convert to Model error', e);
      toast({ title: 'Error', description: 'Failed to convert portfolio', variant: 'destructive' });
    }
  };

  // Bulk actions
  const handleBulkDuplicate = async () => {
    const ids = Array.from(bulkSelectionState.selectedIds || new Set<string>());
    if (ids.length === 0) return;
    let successCount = 0;
    try {
      const uid = user?.id;
      for (const id of ids) {
        const original = (portfolios || []).find((p: any) => p.id === id) as EnhancedPortfolio | undefined;
        if (!original) continue;

        const createdId = await createPortfolio({
          userId: uid || (original as any).user_id,
          name: `${original.name} (Copy)`,
          portfolioType: original.portfolio_type ?? 'model',
          portfolioSize: original.portfolio_size ?? 0,
          status: original.status ?? 'in_progress',
        });

        const allocs = (original.portfolio_allocations || []).map((a: any) => ({
          portfolio_id: createdId,
          asset_id: a.asset_id,
          asset_type: a.asset_type,
          allocation_percentage: a.allocation_percentage ?? 0,
          allocation_value: a.allocation_value ?? null,
          is_value_locked: typeof a.is_value_locked === 'boolean' ? a.is_value_locked : false,
          currency_code: a.currency_code ?? null,
        }));

        if (allocs.length > 0) {
          await insertAllocationsBulk(allocs as any);
        }

        successCount++;
      }

      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      bulkSelectionActions.clearSelection();
      toast({ title: 'Duplicate complete', description: `${successCount} portfolio${successCount !== 1 ? 's' : ''} duplicated` });
    } catch (e) {
      console.error('Bulk duplicate error', e);
      toast({ title: 'Error', description: 'Failed to duplicate portfolios', variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(bulkSelectionState.selectedIds || new Set<string>());
    if (ids.length === 0) return;
    try {
      await deletePortfoliosBulk(ids);
      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      bulkSelectionActions.clearSelection();
      toast({ title: 'Deleted', description: `${ids.length} portfolio${ids.length !== 1 ? 's' : ''} deleted` });
    } catch (e) {
      console.error('Bulk delete error', e);
      toast({ title: 'Error', description: 'Failed to delete selected portfolios', variant: 'destructive' });
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/portfolios/build/${id}`);
  };

  return (
    <PageContainer>
      <div>
      <PortfolioHeader 
        search={search}
        onSearchChange={setSearch}
        onCreatePortfolio={(name, type) => createNewPortfolio(name, type)}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        hideControls={isEmpty}
      />

      {/* Page-level mobile notice for table view */}
      <MobileViewportWarning 
        message="For the best experience viewing your portfolios table, please rotate your device or use a larger screen."
        show={isMobileViewport && viewMode === 'table'}
      />

      {isLoading ? (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="animate-pulse">
              <div className="h-40 bg-muted rounded-xl mb-4" />
              <div className="h-20 bg-muted rounded-xl" />
            </div>
          ))}
        </div>
      ) : (
        (filteredPortfolios?.length || 0) === 0 ? (
          <div className="mt-6">
            <Card className="text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="bg-slate-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
                  <PieChart className="h-8 w-8 text-[#10B981]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Portfolios Yet</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Build, manage, and monitor your investment portfolios with powerful analytics and Ai-powered insights.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          viewMode === 'cards' ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPortfolios?.map((portfolio: EnhancedPortfolio) => {
                // Debug logging before rendering each card (commented out for production)
                // console.log(`Rendering portfolio card for: ${portfolio.id}`, {
                //   name: portfolio.name,
                //   allocations: portfolio.portfolio_allocations?.length || 0,
                //   hasAllocations: portfolio.portfolio_allocations?.length > 0
                // });
                
                return (
                  <PortfolioCard key={portfolio.id} portfolio={portfolio as any} />
                );
              })}
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {!isMobileViewport && (
                <>
                  <PortfolioBulkActionsToolbar
                    selectedCount={bulkSelectionState.selectedCount}
                    onClearSelection={bulkSelectionActions.clearSelection}
                    onDuplicate={handleBulkDuplicate}
                    onDelete={() => setShowBulkDeleteConfirm(true)}
                    onConvertToModel={selectedPortfolio ? handleConvertSelected : undefined}
                    canConvertToModel={canConvertToModel}
                  />

                  <PortfolioTable
                    portfolios={(filteredPortfolios as any) || []}
                    bulkSelection={{
                      isSelected: bulkSelectionActions.isSelected,
                      toggleItem: bulkSelectionActions.toggleItem,
                      toggleAll: bulkSelectionActions.toggleAll,
                    }}
                    onRowClick={handleRowClick}
                  />
                </>
              )}
              <AlertDialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete selected portfolios?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. It will permanently delete the {bulkSelectionState.selectedCount} selected portfolio{bulkSelectionState.selectedCount !== 1 ? 's' : ''} and all their allocations.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" onClick={() => { setShowBulkDeleteConfirm(false); handleBulkDelete(); }}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )
        )
      )}
      </div>
    </PageContainer>
  );
}
